<script lang="ts">
  import { internalLink } from '$lib/utils/internalLink';
  import { onDestroy } from 'svelte';
  import { activitiesApi } from '$lib/api/crm';
  import { createTouchpointDeleter, deletableTouchpoint } from '$lib/utils/deleteTouchpoint';
  import { createInfiniteQuery, useQueryClient } from '@tanstack/svelte-query';
  import { touchpointsApi } from '$lib/api/touchpoints';
  import { getErrorMessage } from '$lib/api/client';
  import { session } from '$lib/state/session.svelte';
  import { confirmAction } from '$lib/state/actionDialog.svelte';
  import { formatIST, formatDate } from '$lib/utils/dates';
  import { istDay, istTime, dayHeading, editableTouchpoint, touchpointStatusLabel, timelineClockLabel, touchpointTypeLabel, outcomeLabel, sentenceCase } from '$lib/utils/touchpoints';
  import type { Activity } from '$lib/types';
  import Composer from './Composer.svelte';

  let { personId, verticalId, companyId }: { personId: number; verticalId?: number; companyId?: number | null } = $props();
  let kind = $state('all'), show = $state(false), editing = $state<Activity | null>(null), mode = $state<'log' | 'plan'>('log'), error = $state('');
  const client = useQueryClient();
  let deleteBusy = $state(false), editorVersion = $state(0);
  let active = true;
  onDestroy(() => { active = false; });
  const deleteLog = createTouchpointDeleter({
    confirm: confirmAction, remove: activitiesApi.remove, invalidate: () => client.invalidateQueries(),
    removeDraft: key => localStorage.removeItem(key), setBusy: value => { deleteBusy = value; },
    setError: err => { error = err ? getErrorMessage(err) : ''; }
  });
  function removeLog(row: Activity) {
    const contactId = personId, version = editorVersion, targetId = row.id, userId = session.me?.user.id;
    return deleteLog(row, session.me?.user.id ?? 0, {
      isCurrent: () => active && personId === contactId && editorVersion === version && session.me?.user.id === userId,
      onDeleted: () => { if (show && editing?.id === targetId) { show = false; editorVersion++; } }
    });
  }
  const events = createInfiniteQuery(() => ({
    queryKey: ['contact-timeline', personId, kind, session.me?.user.email_access],
    initialPageParam: '',
    queryFn: ({ pageParam }) => touchpointsApi.timeline(personId, kind, pageParam),
    getNextPageParam: page => page.next_cursor || undefined
  }));
  const emailAccess = $derived(!session.me?.user.issues_only && !session.me?.user.dispatch_only && (session.isAdmin || !!session.me?.user.email_access));
  const rows = $derived((events.data?.pages.flatMap(p => p.results) ?? []).filter(e => emailAccess || (e.kind !== 'email' && e.kind !== 'emails' && e.activity?.type !== 'email')));
  const filters: Array<[string, string]> = [['all', 'All'], ['conversations', 'Conversations'], ['emails', 'Emails'], ['deals', 'Deals'], ['cases', 'Cases'], ['feedback', 'Feedback'], ['ownership', 'Ownership']];
  const statusChip: Record<string, string> = { scheduled: 'day-chip-info', completed: 'day-chip-ok', cancelled: 'day-chip-warn', no_show: 'day-chip-warn' };

  function open(row: Activity | null, nextMode: 'log' | 'plan') { editorVersion++; editing = row; mode = nextMode; show = true; }
  function canComplete(a: Activity) { return a.assignee_user_id === session.me?.user.id && (!a.scheduled_at || Date.parse(a.scheduled_at) <= Date.now()); }
  async function cancel(row: Activity) {
    if (!await confirmAction(`Cancel “${row.title}”?`)) return;
    try {
      await touchpointsApi.write(`/activities/${row.id}`, { status: 'cancelled' }, 'PUT');
      await client.invalidateQueries();
    } catch (err) {
      error = getErrorMessage(err);
    }
  }
</script>

<section class="timeline">
  <button type="button" class="entry-point" onclick={() => open(null, 'log')}>
    <span class="plus">＋</span> Log a call, meeting or note…
  </button>
  <div class="day-segment scroll" role="group" aria-label="Timeline filters">
    {#each filters as [key, label]}
      {#if key !== 'emails' || emailAccess}<button type="button" aria-pressed={kind === key} onclick={() => kind = key}>{label}</button>{/if}
    {/each}
  </div>

  {#if events.isPending}<p class="tym-loading-state">Loading timeline…</p>
  {:else if events.isError}<p class="tym-error-state" role="alert">{getErrorMessage(events.error)} <button type="button" class="day-mini-btn" onclick={() => events.refetch()}>Retry</button></p>{/if}
  {#if error}<p class="tym-error-state" role="alert">{error}</p>{/if}

  <ol class="events">
    {#each rows as entry, i (entry.id)}
      {#if i === 0 || istDay(entry.at) !== istDay(rows[i - 1].at)}<li class="day">{dayHeading(istDay(entry.at))}</li>{/if}
      <li class="event" class:planned={entry.activity?.status === 'scheduled'}>
        <span class="dot" aria-hidden="true"></span>
        <div class="card">
          <div class="meta">
            <span class="kind">{entry.activity ? touchpointTypeLabel(entry.activity.type) : sentenceCase(entry.kind)}</span>
            <span title={formatIST(entry.at)}>{entry.activity ? timelineClockLabel(entry.activity) : 'Recorded'} {istTime(entry.at)}</span>
            {#if entry.actor_name}<span>· {entry.actor_name}</span>{/if}
          </div>
          <strong class="summary">{entry.summary}</strong>
          {#if entry.activity}
            {@const a = entry.activity}
            <div class="chips">
              <span class="day-chip {statusChip[a.status] ?? 'day-chip-info'}">{touchpointStatusLabel(a.status)}</span>
              {#each a.outcome_tags ?? [] as t}<span class="day-chip day-chip-info">{outcomeLabel(t)}</span>{/each}
            </div>
            {#if a.scheduled_at || a.due_at}
              <p class="detail">{a.status === 'scheduled' ? (a.scheduled_at ? 'Planned for' : 'Due') : 'Was planned for'} {formatIST(a.scheduled_at ?? a.due_at)}{#if a.location}{' · '}{a.location}{/if}</p>
            {/if}
            {#if a.body && a.body !== entry.summary}<p class="body">{a.body}</p>{/if}
            {#if a.outcome && a.outcome !== a.body && a.outcome !== entry.summary}<p class="body">{a.outcome}</p>{/if}
            {#if a.status === 'completed' && a.no_followup}
              <p class="next">No follow-up needed</p>
            {:else if a.next_action}
              <p class="next">{a.status === 'completed' ? 'Next step' : 'Proposed next step'}: <strong>{a.next_action}</strong>{#if a.next_action_date}{' · '}{formatDate(a.next_action_date)}{/if}</p>
            {/if}
            <p class="detail">
              {#if a.assignee_user_id}{a.status === 'scheduled' ? "Who's going" : 'Handled by'}: {a.assignee_name ?? 'Former team member'}{/if}
              {#if a.assignee_user_id && (a.booked_by_name || a.created_by)}{' · '}{/if}
              {#if a.booked_by_name || a.created_by}{a.status === 'scheduled' ? 'Booked' : 'Recorded'} by {a.booked_by_name || a.created_by}{/if}
            </p>
            {#if editableTouchpoint(a)}
              <div class="row-actions">
                {#if a.status === 'scheduled' && canComplete(a)}<button type="button" class="day-mini-btn" onclick={() => open(a, 'log')}>Done — log outcome</button>{/if}
                <button type="button" class="day-mini-btn" onclick={() => open(a, a.status === 'scheduled' ? 'plan' : 'log')}>{a.status === 'scheduled' ? 'Reschedule / edit' : 'Edit'}</button>
                {#if deletableTouchpoint(a)}<button type="button" class="day-mini-btn" disabled={deleteBusy} aria-label={`Delete manual log #${a.id}: ${a.title}`} onclick={() => removeLog(a)}>{deleteBusy ? 'Deleting…' : 'Delete'}</button>{/if}
                {#if a.status === 'scheduled' && (session.isAdmin || a.assignee_user_id === session.me?.user.id)}<button type="button" class="day-mini-btn" onclick={() => cancel(a)}>Cancel plan</button>{/if}
              </div>
            {:else}
              <p class="detail">Synced email · read-only{#if emailAccess}{' · '}<a class="day-link" href="#/emails" use:internalLink={{ tab: 'emails' }}>View in team inbox ↗</a>{/if}</p>
            {/if}
          {/if}
          {#if entry.deal_id || entry.issue_id}
            <div class="row-actions">
              {#if entry.deal_id}<a class="day-link" href={`#/deals/${entry.deal_id}`} use:internalLink={{ tab: 'deals', id: entry.deal_id }}>Open deal ↗</a>{/if}
              {#if entry.issue_id}<a class="day-link" href={`#/issues/${entry.issue_id}`} use:internalLink={{ tab: 'issues', id: entry.issue_id }}>Open case ↗</a>{/if}
            </div>
          {/if}
        </div>
      </li>
    {:else}
      {#if !events.isPending && !events.isError}<li class="tym-empty-state">Nothing recorded yet. Log the first conversation above.</li>{/if}
    {/each}
  </ol>
  {#if events.hasNextPage}<button type="button" class="btn-secondary" disabled={events.isFetchingNextPage} onclick={() => events.fetchNextPage()}>{events.isFetchingNextPage ? 'Loading…' : 'Load older'}</button>{/if}
</section>
{#if show}<Composer {personId} {verticalId} {companyId} activity={editing} {mode} onClose={() => { editorVersion++; show = false; }} />{/if}

<style>
  .timeline { display: grid; gap: 14px; }
  .entry-point { display: flex; align-items: center; gap: 10px; min-height: 52px; padding: 12px 16px; border: 1px dashed var(--tym-teal-300); border-radius: var(--tym-radius-md); background: var(--tym-white); color: var(--tym-teal); font-weight: 600; text-align: left; }
  .entry-point:hover { background: var(--tym-teal-100); }
  .plus { display: inline-flex; width: 26px; height: 26px; align-items: center; justify-content: center; border-radius: 50%; background: var(--tym-lime); color: var(--tym-navy); }
  .events { display: grid; gap: 10px; margin: 0; padding: 0; list-style: none; }
  .day { margin-top: 8px; font-size: 12px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: var(--tym-dark-gray); }
  .event { position: relative; padding-left: 22px; }
  .event::before { content: ''; position: absolute; left: 5px; top: 0; bottom: -10px; width: 2px; background: #e8e8e4; }
  .dot { position: absolute; left: 0; top: 18px; width: 12px; height: 12px; border-radius: 50%; background: var(--tym-teal); border: 2px solid var(--tym-white); box-shadow: 0 0 0 1px var(--tym-teal-300); }
  .planned .dot { background: var(--tym-white); }
  .card { display: grid; gap: 7px; padding: 14px 16px; border-radius: var(--tym-radius-lg); background: var(--tym-white); box-shadow: var(--tym-shadow-1); }
  .planned .card { border: 1px dashed var(--tym-teal-300); box-shadow: none; }
  .meta { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; font-size: 12px; color: var(--tym-dark-gray); }
  .kind { font-weight: 700; color: var(--tym-teal); }
  .summary { color: var(--tym-navy); font-size: 14.5px; font-weight: 600; }
  .chips, .row-actions { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
  .body { white-space: pre-wrap; font-size: 13.5px; color: var(--tym-heavy-carbon); }
  .next { font-size: 13px; padding: 8px 10px; border-radius: var(--tym-radius-sm); background: var(--tym-light-carbon); color: var(--tym-heavy-carbon); }
  .detail { font-size: 12px; color: var(--tym-dark-gray); }
</style>
